import {Queries} from '../../api/queries';
import {formatCurrency} from '../../formatters';
import Table from '../Table';
import {Text} from '@sproutsocial/racine';
import {useQuery} from '@tanstack/react-query';
import {useCallback, useMemo} from 'react';
import {keyBy} from 'shared/src/map';
import type {Sale} from 'shared/src/models';

const HEADERS = [
	{id: 'name', content: 'Item'},
	{id: 'note', content: 'Description'},
	{id: 'quantity', content: 'Quantity'},
	{id: 'faceValue', content: 'Face Value'},
	{id: 'receivedAmount', content: 'Amount Received'},
	{id: 'taxAmount', content: 'Taxes Collected'},
];

interface SalesTableProps {
	sales: Sale[];
}

const SalesTable = ({sales}: SalesTableProps) => {
	const {data: products} = useQuery({
		...Queries.products(),
		select: (products) => keyBy(products, (product) => product.id),
	});

	const totalsRow = useMemo(() => {
		const items = (sales ?? []).flatMap((sale) => sale.items);

		const {sold, received, tax} = items.reduce(
			({sold, received, tax}, item) => {
				return {
					sold: sold + (item.faceValue || item.receivedAmount),
					received: received + item.receivedAmount,
					tax: tax + (item.taxAmount || 0),
				};
			},
			{sold: 0, received: 0, tax: 0},
		);

		return {
			id: 'totals',
			cells: [
				null,
				null,
				null,
				<Text fontWeight='semibold'>{formatCurrency(sold)}</Text>,
				<Text fontWeight='semibold'>{formatCurrency(received)}</Text>,
				<Text fontWeight='semibold'>{formatCurrency(tax)}</Text>,
			],
		};
	}, [sales]);

	const generateRow = useCallback(
		(sale: NonNullable<typeof sales>[0]) => {
			return sale.items.map((item) => {
				const product = item.product ? products?.get(item.product) : null;
				const productVariant = item.productVariant
					? product?.variants.find((variant) => variant.id === item.productVariant)
					: null;

				return {
					id: item.id,
					cells: [
						product &&
							`${product.name} ${productVariant ? ` - ${productVariant.description}` : ''}`,
						item.note,
						item.quantity || null,
						formatCurrency(item.faceValue || item.receivedAmount),
						formatCurrency(item.receivedAmount),
						item.taxAmount ? formatCurrency(item.taxAmount) : null,
					],
				};
			});
		},
		[products],
	);

	return (
		<Table
			id='income-report'
			head={HEADERS}
			fixedRow={totalsRow}
			items={sales ?? []}
			generateRow={generateRow}
		/>
	);
};

export default SalesTable;
